import { useState } from "react";
import { messageErreur } from "./libelles";
import { ErreurBase, appeler, jetonValable } from "./supabase";

/**
 * Les deux gestes qui font sortir le corpus de la base à la main : l'export
 * vers le dépôt (sauvegarde et version de secours de l'application, qui
 * télécharge elle-même le corpus publié), et la recopie des mots réservés vers
 * la table du jeu.
 */
export function Outils() {
  const [jeton, setJeton] = useState<string | null>(null);
  const [copie, setCopie] = useState(false);
  const [recopie, setRecopie] = useState<"repos" | "confirmer" | "fait">("repos");
  const [bilan, setBilan] = useState<{ avant: number; apres: number } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const montrerJeton = async () => {
    setErreur(null);
    try {
      const valeur = await jetonValable();
      setJeton(valeur);
      try {
        await navigator.clipboard.writeText(valeur);
        setCopie(true);
      } catch {
        // Presse-papiers refusé : le jeton reste affiché, à copier à la main.
        setCopie(false);
      }
    } catch (e) {
      setErreur(messageErreur(e instanceof ErreurBase ? e.code : "erreur_inconnue"));
    }
  };

  const recopier = async () => {
    setErreur(null);
    try {
      setBilan(await appeler<{ avant: number; apres: number }>("admin_recopier_vers_enigmes"));
      setRecopie("fait");
    } catch (e) {
      setErreur(messageErreur(e instanceof ErreurBase ? e.code : "erreur_inconnue"));
      setRecopie("repos");
    }
  };

  return (
    <section>
      <h1>Export et jeu</h1>
      {erreur !== null && (
        <p role="alert" className="erreur">
          {erreur}
        </p>
      )}

      <section className="panneau" aria-labelledby="titre-export">
        <h2 id="titre-export">Exporter vers le dépôt</h2>
        <p>
          Les joueurs n'attendent pas l'export : l'application télécharge le corpus publié et joue une correction au
          lancement suivant. Le dépôt en garde une copie — sauvegarde, revue par diff, et version de secours embarquée
          pour un premier lancement sans réseau. Avant de publier une nouvelle version de l'application, lance sur ta
          machine, à la racine du dépôt :
        </p>
        <pre>LEXIREVEAL_JETON_ADMIN=… pnpm --filter @lexireveal/content-schema base:exporter</pre>
        <button type="button" className="principal" onClick={() => void montrerJeton()}>
          Jeton pour l'export
        </button>
        {jeton !== null && (
          <>
            <p role="status" className={copie ? "succes" : undefined}>
              {copie ? "Jeton copié. " : ""}Valable une heure : ne le partage pas, il ouvre le corpus à qui le détient.
            </p>
            <label>
              Jeton d'accès
              <textarea readOnly rows={3} value={jeton} onFocus={(e) => e.target.select()} />
            </label>
          </>
        )}
      </section>

      <section className="panneau" aria-labelledby="titre-jeu">
        <h2 id="titre-jeu">Recopier vers le jeu</h2>
        <p>
          Le serveur de jeu ne lit que les mots réservés aux parties classées (mot du jour, duels), absents de
          l'application. Il ne suit pas tout seul : après avoir corrigé un mot réservé, recopie pour que la partie
          suivante le serve. L'ordre des mots du jour ne change pas tant que la réserve reste la même.
        </p>
        {recopie === "repos" && (
          <button type="button" onClick={() => setRecopie("confirmer")}>
            Recopier le corpus publié vers le jeu
          </button>
        )}
        {recopie === "confirmer" && (
          <div className="actions">
            <button type="button" className="danger" onClick={() => void recopier()}>
              Oui, recopier la réserve vers le jeu
            </button>
            <button type="button" onClick={() => setRecopie("repos")}>
              Annuler
            </button>
          </div>
        )}
        {recopie === "fait" && bilan !== null && (
          <p role="status" className="succes">
            Recopie faite : {bilan.avant} mots classés avant, {bilan.apres} maintenant.
          </p>
        )}
      </section>
    </section>
  );
}
